// UserContext.js - 토큰 기반 즉시 렌더링 + 진짜 로딩 시에만 스켈레톤
import React, { createContext, useContext, useState, useEffect } from 'react';
import jwt_decode from 'jsonwebtoken/decode';
import { getUserInfo } from '../utils/apiCache';
import { syncUserDataToAdrop } from '../services/adropService';

const UserContext = createContext();

// 🚀 초기 토큰에서 userID 추출 (동기적으로 처리)
const getInitialTokenData = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return { tokenUserID: "", tokenUserNo: "" };
    }

    const decodedToken = jwt_decode(token);
    const currentTime = Date.now() / 1000;

    if (decodedToken.exp <= currentTime) {
      // 토큰 만료 시 정리
      localStorage.removeItem('token');
      localStorage.removeItem('user_id');
      return { tokenUserID: "", tokenUserNo: "" };
    }

    return {
      tokenUserID: decodedToken.user_id || "",
      tokenUserNo: decodedToken.user_no || ""
    };
  } catch (error) {
    // 토큰 디코딩 실패 시 빈 값 반환
    return { tokenUserID: "", tokenUserNo: "" };
  }
};

export const UserProvider = ({ children }) => {
  // 🚀 초기 상태를 함수로 설정하여 localStorage에서 즉시 토큰 읽기
  const initialTokenData = getInitialTokenData();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false); // 🚀 false로 시작 - 즉시 렌더링 허용
  const [tokenUserID, setTokenUserID] = useState(initialTokenData.tokenUserID);
  const [tokenUserNo, setTokenUserNo] = useState(initialTokenData.tokenUserNo);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeUser = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          // 토큰이 없으면 상태 초기화 (초기 상태와 다를 때만 업데이트)
          if (tokenUserID !== "" || tokenUserNo !== "") {
            setTokenUserID("");
            setTokenUserNo("");
            setUserData(null);
          }
          setLoading(false);
          return;
        }

        // 토큰에서 즉시 userID 추출 (API 호출 없이)
        const decodedToken = jwt_decode(token);
        const currentTime = Date.now() / 1000;

        if (decodedToken.exp <= currentTime) {
          // 토큰 만료 시 정리
          localStorage.removeItem('token');
          localStorage.removeItem('user_id');
          if (tokenUserID !== "" || tokenUserNo !== "") {
            setTokenUserID("");
            setTokenUserNo("");
            setUserData(null);
          }
          setLoading(false);
          return;
        }

        // 🚀 토큰에서 즉시 데이터 추출 (변경된 경우에만 업데이트)
        const newTokenUserID = decodedToken.user_id || "";
        const newTokenUserNo = decodedToken.user_no || "";
        
        if (tokenUserID !== newTokenUserID) {
          setTokenUserID(newTokenUserID);
        }
        if (tokenUserNo !== newTokenUserNo) {
          setTokenUserNo(newTokenUserNo);
        }
        
        // 🚀 API 병렬화: loading을 false로 유지하고 백그라운드에서 API 호출
        // ✅ 다른 Context들(GlobalTimer, GlobalPoints)이 tokenUserID를 받아 
        //    즉시 병렬로 각자의 API 호출 시작 가능 (워터폴 방지)
        // userData가 없을 때만 API 호출 (중복 호출 방지)
        if (!userData && newTokenUserID) {
          // 🚀 병렬 실행: getUserInfo와 다른 Context의 API들이 동시에 실행됨
          getUserInfo(newTokenUserID, token)
            .then(data => {
              setUserData(data);
              // 🚀 Adrop 동기화는 백그라운드에서 실행 (blocking 없음)
              syncUserDataToAdrop(newTokenUserID, data).catch(error => {
                console.warn('Adrop 사용자 데이터 동기화 실패 (무시됨):', error);
              });
            })
            .catch(apiError => {
              console.error('사용자 정보 API 호출 실패:', apiError);
              // API 실패해도 토큰 정보는 있으므로 계속 진행
            });
        }

      } catch (tokenError) {
        console.error('토큰 처리 실패:', tokenError);
        localStorage.removeItem('token');
        localStorage.removeItem('user_id');
        if (tokenUserID !== "" || tokenUserNo !== "") {
          setTokenUserID("");
          setTokenUserNo("");
          setUserData(null);
        }
        setError(tokenError.message);
        setLoading(false);
      }
    };

    initializeUser();
    
    // 🚨 토큰 변경 감지 (같은 탭에서 계정 전환)
    const handleTokenChange = () => {
      initializeUser();
    };
    
    // Custom event 리스너 (App.js에서 발생시킬 수 있음)
    window.addEventListener('tokenChanged', handleTokenChange);
    
    return () => {
      window.removeEventListener('tokenChanged', handleTokenChange);
    };
  }, []);

  // 🚀 Context value 메모이제이션: 불필요한 리렌더링 방지
  // setState 함수들은 React가 stable identity를 보장하므로 의존성 배열에서 제외
  const value = React.useMemo(() => ({
    userData,
    loading,
    error,
    tokenUserID,
    tokenUserNo,
    setUserData,
    setLoading
  }), [userData, loading, error, tokenUserID, tokenUserNo]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
